import React, { useState, useEffect, useRef } from 'react'
import { Box, Container, Typography, IconButton, Fade } from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  PlayArrow,
} from '@mui/icons-material'
import type { ImageCarouselContent } from '@/data/types'

interface ImageCarouselSectionProps {
  content: ImageCarouselContent
  autoPlayEnabled?: boolean
  testId?: string
}

export const ImageCarouselSection: React.FC<ImageCarouselSectionProps> = ({
  content,
  autoPlayEnabled = true,
  testId = 'image-carousel',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(autoPlayEnabled)
  const [isReducedMotion, setIsReducedMotion] = useState(false)
  const [touchStartX, setTouchStartX] = useState<number | null>(null)
  const [touchEndX, setTouchEndX] = useState<number | null>(null)
  const timerRef = useRef<number | null>(null)

  const images = content.images
  const currentImage = images[currentIndex]

  // Detect prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setIsReducedMotion(mediaQuery.matches)

    const handler = (e: MediaQueryListEvent) => setIsReducedMotion(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  // Auto-play effect
  useEffect(() => {
    if (isPlaying && !isReducedMotion) {
      timerRef.current = window.setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % images.length)
      }, content.autoPlayInterval || 5000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isPlaying, isReducedMotion, images.length, content.autoPlayInterval])

  // Navigation handlers
  const handleNext = () => {
    setIsPlaying(false)
    setCurrentIndex(prev => (prev + 1) % images.length)
  }

  const handlePrevious = () => {
    setIsPlaying(false)
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length)
  }

  const handleJumpToSlide = (index: number) => {
    setIsPlaying(false)
    setCurrentIndex(index)
  }

  const handleTogglePlay = () => {
    setIsPlaying(prev => !prev)
  }

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        handlePrevious()
        break
      case 'ArrowRight':
        event.preventDefault()
        handleNext()
        break
      case 'Home':
        event.preventDefault()
        handleJumpToSlide(0)
        break
      case 'End':
        event.preventDefault()
        handleJumpToSlide(images.length - 1)
        break
      case ' ':
        event.preventDefault()
        handleTogglePlay()
        break
    }
  }

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX)
    setTouchEndX(null)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return

    const distance = touchStartX - touchEndX
    const isSignificantSwipe = Math.abs(distance) > 50

    if (isSignificantSwipe) {
      if (distance > 0) {
        handleNext()
      } else {
        handlePrevious()
      }
    }

    setTouchStartX(null)
    setTouchEndX(null)
  }

  return (
    <Box
      component="section"
      role="region"
      aria-roledescription="carousel"
      aria-label={content.title}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      sx={{
        py: { xs: 6, md: 8 },
        '&:focus': {
          outline: '3px solid #962029',
          outlineOffset: '2px',
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: '#211e4c',
              mb: 1,
            }}
          >
            {content.title}
          </Typography>
          <Typography
            variant="h3"
            component="h3"
            sx={{
              fontSize: { xs: '1rem', md: '1.25rem' },
              fontWeight: 400,
              color: 'text.secondary',
            }}
          >
            {content.subtitle}
          </Typography>
        </Box>

        {/* Carousel Container */}
        <Box sx={{ position: 'relative' }}>
          {/* Pause/Play Button */}
          <IconButton
            onClick={handleTogglePlay}
            aria-label={
              isPlaying
                ? 'Pause automatic slideshow'
                : 'Play automatic slideshow'
            }
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 2,
              bgcolor: 'rgba(33, 30, 76, 0.7)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(33, 30, 76, 0.9)',
              },
              '&:focus': {
                outline: '3px solid #962029',
                outlineOffset: '2px',
              },
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          {/* Image Display */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: { xs: '4/3', md: '16/9' },
              overflow: 'hidden',
              borderRadius: 2,
              bgcolor: '#f5f5f5',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <Fade
              in={true}
              timeout={content.transitionDuration || 600}
              key={currentImage.id}
            >
              <Box
                component="img"
                src={currentImage.src}
                alt={currentImage.alt}
                loading="lazy"
                onError={e => {
                  const target = e.target as HTMLImageElement
                  target.alt = 'Image failed to load'
                  target.style.display = 'flex'
                  target.style.alignItems = 'center'
                  target.style.justifyContent = 'center'
                  target.style.backgroundColor = '#e0e0e0'
                }}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            </Fade>

            {/* Previous Button */}
            <IconButton
              onClick={handlePrevious}
              aria-label="Previous image"
              size="large"
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(33, 30, 76, 0.7)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(33, 30, 76, 0.9)',
                },
                '&:focus': {
                  outline: '3px solid #962029',
                  outlineOffset: '2px',
                },
              }}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>

            {/* Next Button */}
            <IconButton
              onClick={handleNext}
              aria-label="Next image"
              size="large"
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(33, 30, 76, 0.7)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(33, 30, 76, 0.9)',
                },
                '&:focus': {
                  outline: '3px solid #962029',
                  outlineOffset: '2px',
                },
              }}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          </Box>

          {/* Position Indicators */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: 2,
            }}
            role="tablist"
            aria-label="Image position indicators"
          >
            {images.map((image, index) => (
              <IconButton
                key={image.id}
                onClick={() => handleJumpToSlide(index)}
                aria-label={`Go to image ${index + 1} of ${images.length}`}
                aria-current={index === currentIndex ? 'true' : undefined}
                size="small"
                role="tab"
                sx={{
                  width: 12,
                  height: 12,
                  minWidth: 0,
                  minHeight: 0,
                  p: 0,
                  borderRadius: '50%',
                  bgcolor: index === currentIndex ? '#962029' : '#ccc',
                  '&:hover': {
                    bgcolor: index === currentIndex ? '#7a1a21' : '#999',
                  },
                  '&:focus': {
                    outline: '2px solid #962029',
                    outlineOffset: '2px',
                  },
                }}
              />
            ))}
          </Box>

          {/* Caption */}
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mt: 3,
              color: 'text.primary',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
            }}
          >
            {currentImage.caption}
          </Typography>

          {/* ARIA Live Region */}
          <Box
            aria-live="polite"
            aria-atomic="true"
            sx={{
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            Image {currentIndex + 1} of {images.length}: {currentImage.caption}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default ImageCarouselSection
